/**
 * Persists analyzer coaching resume state across signup/login (localStorage).
 * Analysis payload remains in sessionStorage (`analyzer_session_state`).
 */

export const ANALYZER_GUEST_SESSION_KEY = 'lina_analyzer_guest_session_v1'

export type AnalyzerPendingAction = 'coaching' | null

export interface AnalyzerGuestSession {
  version: 1
  savedAt: string
  /** auth user / account id when analysis was created */
  accountId?: string | null
  pendingAction: AnalyzerPendingAction
}

export function createEmptyAnalyzerGuestSession(): AnalyzerGuestSession {
  return {
    version: 1,
    savedAt: new Date().toISOString(),
    pendingAction: null,
  }
}

export function loadAnalyzerGuestSession(): AnalyzerGuestSession | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(ANALYZER_GUEST_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as AnalyzerGuestSession
    if (parsed?.version !== 1) return null
    return parsed
  } catch {
    return null
  }
}

export function saveAnalyzerGuestSession(
  state: Partial<AnalyzerGuestSession> & Pick<AnalyzerGuestSession, 'pendingAction'>,
): void {
  if (typeof window === 'undefined') return
  try {
    const existing = loadAnalyzerGuestSession() ?? createEmptyAnalyzerGuestSession()
    localStorage.setItem(
      ANALYZER_GUEST_SESSION_KEY,
      JSON.stringify({
        ...existing,
        ...state,
        version: 1 as const,
        savedAt: new Date().toISOString(),
      }),
    )
  } catch (err) {
    console.warn('[analyzer-guest-session] Failed to persist session', err)
  }
}

export function clearAnalyzerGuestSession(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(ANALYZER_GUEST_SESSION_KEY)
  } catch {
    // ignore
  }
}

export function hasAnalyzerResumePending(): boolean {
  const session = loadAnalyzerGuestSession()
  return session?.pendingAction === 'coaching'
}

export function getAnalyzerResumePath(): string {
  return '/analyze/results'
}

export function shouldSyncAnalyzerSessionOwner(
  sessionAccountId: string | null | undefined,
  currentAccountId: string | null | undefined,
): boolean {
  if (!currentAccountId || !sessionAccountId) return false
  return sessionAccountId === currentAccountId
}
